import React, { useState, useEffect } from 'react';
import { useNotifications, useFormGuard, useLiveRefresh, useDebouncedCallback } from '@byscott-io/corebyscott';
import { useWaldoEvent } from '@byscott-io/where-is-waldo';
import { shoppingLists, shoppingListItems, shoppingListRecipes, recipes, recipeCategories } from '../api/client';
import Modal from '../components/common/Modal';
import CategoryFilter from '../components/common/CategoryFilter';

export default function ShoppingPage() {
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [availableRecipes, setAvailableRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [hideHaveItems, setHideHaveItems] = useState(false);
  const notifications = useNotifications();

  useEffect(() => {
    loadLists();
  }, []);

  // Live shopping (#626): two people shopping see each other's check-offs and
  // new lists/items live. Defer while a modal is open; debounced + coalesced.
  useFormGuard(showNewModal || showRecipeModal);
  const refresh = useLiveRefresh(useDebouncedCallback(() => {
    loadLists();
    if (selectedList?.id) loadListDetails(selectedList.id);
  }, 400));
  useWaldoEvent(
    [
      "shopping_list_created", "shopping_list_update", "shopping_list_destroyed",
      "shopping_list_item_created", "shopping_list_item_update", "shopping_list_item_destroyed",
    ],
    refresh
  );

  const loadLists = async () => {
    try {
      setLoading(true);
      const response = await shoppingLists.list();
      setLists(response.data.shopping_lists || []);
    } catch {
      notifications.error('Failed to load shopping lists');
    } finally {
      setLoading(false);
    }
  };

  const loadListDetails = async (id) => {
    try {
      const response = await shoppingLists.get(id);
      setSelectedList(response.data.shopping_list);
    } catch {
      notifications.error('Failed to load list details');
    }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    try {
      const response = await shoppingLists.create({ shopping_list: { name: newListName } });
      setLists([response.data.shopping_list, ...lists]);
      setNewListName('');
      setShowNewModal(false);
      setSelectedList(response.data.shopping_list);
      notifications.success('Shopping list created');
    } catch {
      notifications.error('Failed to create list');
    }
  };

  const handleDeleteList = async (id) => {
    if (!confirm('Delete this shopping list?')) return;
    try {
      await shoppingLists.delete(id);
      setLists(lists.filter(l => l.id !== id));
      if (selectedList?.id === id) setSelectedList(null);
      notifications.success('List deleted');
    } catch {
      notifications.error('Failed to delete list');
    }
  };

  const handleAddRecipe = async () => {
    try {
      const [recipesRes, categoriesRes] = await Promise.all([
        recipes.list(),
        recipeCategories.list(),
      ]);
      setAvailableRecipes(recipesRes.data.recipes || []);
      setCategories(categoriesRes.data.categories || []);
      setRecipeSearch('');
      setSelectedCategories([]);
      setShowRecipeModal(true);
    } catch {
      notifications.error('Failed to load recipes');
    }
  };

  // Filter recipes by selected categories and search
  const filteredRecipes = availableRecipes.filter((recipe) => {
    // Filter by search text
    if (recipeSearch.trim() && !recipe.title.toLowerCase().includes(recipeSearch.toLowerCase())) {
      return false;
    }
    // Filter by categories
    if (selectedCategories.length > 0) {
      const recipeCatIds = recipe.categories?.map((c) => c.id) || [];
      if (!selectedCategories.some((id) => recipeCatIds.includes(id))) {
        return false;
      }
    }
    return true;
  });

  const handleSelectRecipe = async (recipeId) => {
    try {
      await shoppingListRecipes.add(selectedList.id, recipeId, 1);
      loadListDetails(selectedList.id);
      setShowRecipeModal(false);
      notifications.success('Recipe added');
    } catch {
      notifications.error('Failed to add recipe');
    }
  };

  const handleRemoveRecipe = async (slrId) => {
    try {
      await shoppingListRecipes.remove(selectedList.id, slrId);
      loadListDetails(selectedList.id);
    } catch {
      notifications.error('Failed to remove recipe');
    }
  };

  const handleGenerateItems = async () => {
    try {
      const response = await shoppingLists.generateFromRecipes(selectedList.id);
      setSelectedList(response.data.shopping_list);
      notifications.success('Items generated from recipes');
    } catch {
      notifications.error('Failed to generate items');
    }
  };

  const handleToggleChecked = async (item) => {
    try {
      await shoppingListItems.update(selectedList.id, item.id, {
        shopping_list_item: { checked: !item.checked }
      });
      setSelectedList(prev => ({
        ...prev,
        items: prev.items.map(i =>
          i.id === item.id ? { ...i, checked: !i.checked } : i
        ),
        checked_count: item.checked ? prev.checked_count - 1 : prev.checked_count + 1
      }));
    } catch {
      notifications.error('Failed to update item');
    }
  };

  const handleMarkAsHave = async (item) => {
    // Mark item as "already have" - removes from active shopping list view
    try {
      await shoppingListItems.update(selectedList.id, item.id, {
        shopping_list_item: { from_cupboard: true }
      });
      setSelectedList(prev => ({
        ...prev,
        items: prev.items.map(i =>
          i.id === item.id ? { ...i, from_cupboard: true } : i
        )
      }));
      notifications.success('Marked as already have');
    } catch {
      notifications.error('Failed to update item');
    }
  };

  const handleUnmarkAsHave = async (item) => {
    try {
      await shoppingListItems.update(selectedList.id, item.id, {
        shopping_list_item: { from_cupboard: false }
      });
      setSelectedList(prev => ({
        ...prev,
        items: prev.items.map(i =>
          i.id === item.id ? { ...i, from_cupboard: false } : i
        )
      }));
    } catch {
      notifications.error('Failed to update item');
    }
  };

  const handleAddManualItem = async (e) => {
    e.preventDefault();
    if (!newItemName.trim()) return;
    try {
      const response = await shoppingListItems.create(selectedList.id, {
        shopping_list_item: { name: newItemName.trim() }
      });
      setSelectedList(prev => ({
        ...prev,
        items: [...prev.items, response.data.shopping_list_item],
        items_count: prev.items_count + 1
      }));
      setNewItemName('');
    } catch {
      notifications.error('Failed to add item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await shoppingListItems.delete(selectedList.id, itemId);
      setSelectedList(prev => ({
        ...prev,
        items: prev.items.filter(i => i.id !== itemId),
        items_count: prev.items_count - 1
      }));
    } catch {
      notifications.error('Failed to delete item');
    }
  };

  const handleCompleteList = async () => {
    try {
      const response = await shoppingLists.update(selectedList.id, {
        shopping_list: { status: 'completed' }
      });
      setSelectedList(response.data.shopping_list);
      setLists(lists.map(l => l.id === selectedList.id ? response.data.shopping_list : l));
      notifications.success('List marked as completed');
    } catch {
      notifications.error('Failed to update list');
    }
  };

  // Filter items based on "hide already have" toggle
  const visibleItems = selectedList?.items?.filter(item =>
    hideHaveItems ? !item.from_cupboard : true
  ) || [];

  const needToBuyCount = selectedList?.items?.filter(i => !i.from_cupboard && !i.checked).length || 0;
  const alreadyHaveCount = selectedList?.items?.filter(i => i.from_cupboard).length || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Shopping Lists</h1>
        <button
          onClick={() => setShowNewModal(true)}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <span className="mr-2">+</span>
          New List
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lists Sidebar */}
        <div className="space-y-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">Your Lists</h2>
          {lists.length > 0 ? (
            <div className="space-y-2">
              {lists.map((list) => (
                <div
                  key={list.id}
                  onClick={() => loadListDetails(list.id)}
                  className={`p-4 rounded-lg cursor-pointer transition-colors ${
                    selectedList?.id === list.id
                      ? 'bg-indigo-100 dark:bg-indigo-900/30 border-2 border-indigo-500'
                      : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{list.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {list.checked_count}/{list.items_count} checked
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      list.status === 'completed'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}>
                      {list.status}
                    </span>
                  </div>
                  {list.items_count > 0 && (
                    <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 transition-all"
                        style={{ width: `${list.progress_percent}%` }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              No shopping lists yet. Create one to get started!
            </p>
          )}
        </div>

        {/* Selected List Details */}
        <div className="lg:col-span-2">
          {selectedList ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              {/* Header */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {selectedList.name}
                  </h2>
                  <div className="flex gap-2">
                    {selectedList.status === 'active' && (
                      <button
                        onClick={handleCompleteList}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Mark Complete
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteList(selectedList.id)}
                      className="px-3 py-1 text-sm text-red-600 border border-red-600 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Recipe Management */}
                <div className="mt-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Recipes:</span>
                    {selectedList.recipes?.map((slr) => (
                      <span
                        key={slr.id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-sm"
                      >
                        {slr.recipe_title}
                        {slr.servings_multiplier > 1 && ` (${slr.servings_multiplier}x)`}
                        <button
                          onClick={() => handleRemoveRecipe(slr.id)}
                          className="ml-1 hover:text-red-600"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <button
                      onClick={handleAddRecipe}
                      className="px-2 py-1 text-sm text-indigo-600 border border-indigo-600 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                    >
                      + Add Recipe
                    </button>
                  </div>

                  {selectedList.recipes?.length > 0 && (
                    <div className="mt-3">
                      <button
                        onClick={handleGenerateItems}
                        className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                      >
                        Generate Items from Recipes
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats and Filter */}
              {selectedList.items?.length > 0 && (
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-900 dark:text-white">{needToBuyCount}</span> to buy
                    {alreadyHaveCount > 0 && (
                      <span className="ml-3">
                        <span className="font-medium text-green-600 dark:text-green-400">{alreadyHaveCount}</span> already have
                      </span>
                    )}
                  </div>
                  {alreadyHaveCount > 0 && (
                    <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={hideHaveItems}
                        onChange={(e) => setHideHaveItems(e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      Hide items I have
                    </label>
                  )}
                </div>
              )}

              {/* Add Manual Item */}
              <form onSubmit={handleAddManualItem} className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add item manually..."
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                  <button
                    type="submit"
                    disabled={!newItemName.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </form>

              {/* Items */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {visibleItems.length > 0 ? (
                  visibleItems.map((item) => (
                    <div
                      key={item.id}
                      className={`p-4 flex items-center gap-3 ${
                        item.from_cupboard ? 'bg-green-50 dark:bg-green-900/10 opacity-60' : ''
                      }`}
                    >
                      {/* Checkbox for shopping */}
                      <button
                        onClick={() => handleToggleChecked(item)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                          item.checked
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'border-gray-300 dark:border-gray-600 hover:border-green-500'
                        }`}
                      >
                        {item.checked && (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>

                      {/* Item name */}
                      <div className="flex-1">
                        <span className={`text-gray-900 dark:text-white ${item.checked ? 'line-through text-gray-400' : ''}`}>
                          {item.quantity && `${item.quantity} `}
                          {item.unit && `${item.unit} `}
                          {item.name}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {item.from_cupboard ? (
                          <button
                            onClick={() => handleUnmarkAsHave(item)}
                            className="text-xs px-2 py-1 text-green-600 border border-green-600 rounded hover:bg-green-50 dark:hover:bg-green-900/20"
                          >
                            Need it
                          </button>
                        ) : (
                          <button
                            onClick={() => handleMarkAsHave(item)}
                            className="text-xs px-2 py-1 text-gray-500 border border-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                            title="I already have this"
                          >
                            Have it
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteItem(item.id)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                    {hideHaveItems && alreadyHaveCount > 0
                      ? 'All remaining items are hidden (you marked them as already have).'
                      : 'No items yet. Add recipes and generate items, or add items manually.'}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                Select a shopping list to view details, or create a new one.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* New List Modal */}
      <Modal isOpen={showNewModal} onClose={() => setShowNewModal(false)} title="New Shopping List">
        <div className="space-y-4">
          <input
            type="text"
            placeholder="List name (e.g., Weekly Groceries)"
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowNewModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateList}
              disabled={!newListName.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              Create
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Recipe Modal */}
      <Modal isOpen={showRecipeModal} onClose={() => setShowRecipeModal(false)} title="Add Recipe to List" size="lg">
        <div className="space-y-4">
          {/* Search and Filter */}
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Search recipes..."
              value={recipeSearch}
              onChange={(e) => setRecipeSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
            {categories.length > 0 && (
              <CategoryFilter
                categories={categories}
                selected={selectedCategories}
                onChange={setSelectedCategories}
              />
            )}
          </div>

          {/* Recipe List */}
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {filteredRecipes.length > 0 ? (
              filteredRecipes.map((recipe) => (
                <button
                  key={recipe.id}
                  onClick={() => handleSelectRecipe(recipe.id)}
                  className="w-full p-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                  <div className="font-medium text-gray-900 dark:text-white">{recipe.title}</div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    {recipe.total_time_minutes > 0 && (
                      <span>{recipe.total_time_minutes} min</span>
                    )}
                    {recipe.categories?.slice(0, 3).map((cat) => (
                      <span
                        key={cat.id}
                        className="px-1.5 py-0.5 text-xs rounded"
                        style={{ backgroundColor: cat.color + '20', color: cat.color }}
                      >
                        {cat.name}
                      </span>
                    ))}
                  </div>
                </button>
              ))
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                {availableRecipes.length > 0 ? 'No recipes match your filters' : 'No recipes available. Create some recipes first!'}
              </p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
